// Simple clock interface, primarily to help with tests.
export interface Clock {
  // Returns the current wall clock time, in epoch milliseconds.
  now(): number;
}

// Default Clock implementation that simply delegates to Date.now().
// We _could_ use Date directly to implement this, but doing so resulted in issues with Cypress tests.
//   The issue is that we do some trickery in cypres/utils.js to override the Date implementation, but it's hard to
//   get it done correctly during app startup, resulting in racy tests if we cache the Date reference.
export class WallClock implements Clock {
  now(): number {
    return Date.now();
  }
}

// Simple test clock implementation.
export class TestClock implements Clock {
  private _now: number = 0;

  constructor(epochMillis: number = 0) {
    this.set(epochMillis);
  }
  now(): number {
    return this._now;
  }
  set(epochMillis: number) {
    this._now = epochMillis;
  }
  advance(ms: number) {
    this._now += ms;
  }
}

// There's currently a mirror implementation of Ticker in eventqueue.ts, used by various /recording consumers.
// We try to maintain a strong firewall between code in /recording and everywhere else,
// just because there are much stronger restrictions on what will work in the range of supported recording devices,
// and the much more restricted range of playback devices.
// (so basically, should be OK for this impl to skew/change/whatever for app purposes, but dont mess with
// RealTicker in /recording unless you really have to)
export class AppTicker {
  private _handle = -1;

  constructor(private _interval: number) {}

  oneShot(tick: () => void) {
    setTimeout(tick, this._interval);
  }

  start(tick: () => void) {
    if (this._handle == -1) {
      this._handle = window.setInterval(tick, this._interval);
    }
  }

  stop() {
    if (this._handle != -1) {
      window.clearInterval(this._handle);
      this._handle = -1;
    }
  }
}

export interface CountdownTimerEvents {
  onStarted(millis: number): void;
  onStopped(millis: number): void;
  onTick(millis: number): void;
  onTimeout(): void;
}

export class CountdownTimer {
  private _valueMillis: number = 0;
  private _ticker: AppTicker;
  private _tick: () => void;

  constructor(private _del: CountdownTimerEvents, private _interval: number) {
    this._ticker = new AppTicker(this._interval);
    this._tick = () => {
      this._valueMillis -= this._interval;
      this._del.onTick(this._valueMillis);
      if (!this._valueMillis) {
        this._ticker.stop();
        this._del.onTimeout();
      }
    };
  }

  start() {
    this._ticker.start(this._tick);
    this._del.onStarted(this._valueMillis);
  }

  stop() {
    this._ticker.stop();
    this._del.onStopped(this._valueMillis);
  }

  reset(startVal: number) {
    this._valueMillis = startVal;
  }
}
